import React from "react";
import { Box, Icon, Text, type IconName, ROW_RUNGS } from "@salestrak/ui";
import { urlHref } from "@salestrak/custom-fields";

import { RowAction, copyText, openHref } from "./row-actions";
import type { Omittable } from "@salestrak/ui";

export type DetailFieldRowProps = Omittable<{
  icon?: IconName;
  label: string;
  value?: string | null;
  /** Shown muted when `value` is empty. Default "None". */
  emptyText?: string;
  /** "email" adds copy + open-mail; "phone" adds copy + open-call; "url" adds
   *  copy + open-in-new-tab (open only when the text is a web address). */
  link?: "email" | "phone" | "url";
  /** Copy-only action. Ignored when `link` is set (link already copies). */
  copyable?: boolean;
  /** Clamps the value to N lines. */
  truncate?: 1 | 2 | 3;
  /** Default "sm" ("2xs" under `compact`). */
  labelTextSize?: "2xs" | "xs" | "sm";
  /** Default "sm" ("xs" under `compact`). */
  valueTextSize?: "2xs" | "xs" | "sm";
  /** Compact, icon-less density for the hover preview's property sheet. */
  compact?: boolean;
  /** Icon-led row: drop the label column and let the icon carry the meaning
   *  (the preview cards' facts grammar). `label` stays for accessibility. */
  hideLabel?: boolean;
  /** A node in the 20px leading slot instead of `icon` — the value's OWN
   *  visual where it is the row's one glyph (a stage's pie, a status' pulse
   *  in its hue), never beside an icon. `hideLabel` rows only. */
  leading?: React.ReactNode;
  /** Width (px) of the centered leading icon slot under `hideLabel` — a fixed
   *  rail so mixed glyph widths keep the values on one line. Default 20. The
   *  rail is always one value line (20px) tall, so `align="start"` rows keep
   *  the glyph on the first line's centerline instead of hugging the row top. */
  leadingWidth?: number;
  /** Default "center"; pass "start" when the value is a tall vertical stack. */
  align?: "center" | "start";
  /** Value pinned to the row's right edge. Always shown. */
  trailing?: React.ReactNode;
  /** Overrides the `link`/`copyable`-derived actions. Use with `children`.
   *  Actions sit in flow right after the value (not pinned to the row's far
   *  edge) and reveal on row hover, focus, or touch. */
  actions?: React.ReactNode;
  /** Custom value content; when present, `value`/`link`/`copyable` are ignored. */
  children?: React.ReactNode;
  /** Runs on each built-in row action ("email", "call", "open", "copy"), so
   *  a host can send its own analytics event. */
  onContact?: (action: "email" | "call" | "open" | "copy") => void;
}>;

const LABEL_WIDTH = 80;
/** One-line row rung; the leading rail is this tall too. */
const ROW_HEIGHT = ROW_RUNGS.line;

export function DetailFieldRow({
  icon,
  label,
  value,
  emptyText = "None",
  link,
  copyable,
  truncate,
  labelTextSize,
  valueTextSize,
  compact = false,
  hideLabel = false,
  leading,
  leadingWidth = 20,
  align = "center",
  trailing,
  actions: actionsProp,
  children,
  onContact,
}: DetailFieldRowProps) {
  const resolvedLabelSize = labelTextSize ?? (compact ? "2xs" : "sm");
  const resolvedValueSize = valueTextSize ?? (compact ? "xs" : "sm");

  const hasValue = !!value && value.trim().length > 0;
  const actions =
    actionsProp ??
    (!children && hasValue ? buildActions(value!, label, link, copyable, onContact) : null);

  return (
    <Box
      direction="row"
      align={align}
      gap="sm"
      group={actions ? "row" : undefined}
      minHeight={ROW_HEIGHT}
    >
      {hideLabel && (leading || icon) ? (
        <Box
          width={leadingWidth}
          // The row's rung tall, so the glyph centers on the FIRST value
          // line even when `align="start"` top-aligns the rail beside a
          // tall value (the value wrapper pads its first line onto the same
          // centerline).
          height={ROW_HEIGHT}
          direction="row"
          align="center"
          justify="center"
          shrink={false}
          aria-label={label}
        >
          {leading ?? <Icon name={icon!} size="sm" color="muted" />}
        </Box>
      ) : icon ? (
        <Icon name={icon} size="sm" color="muted" />
      ) : null}
      {hideLabel ? null : (
        <Box width={LABEL_WIDTH} shrink={false}>
          <Text
            size={resolvedLabelSize}
            weight={resolvedLabelSize === "sm" ? undefined : "medium"}
            variant="muted"
          >
            {label}
          </Text>
        </Box>
      )}
      {/* Value + actions hug together so the actions read as the value's
          own, not as row chrome. The actions stay mounted (opacity reveal)
          so revealing them never re-wraps or re-truncates the value. */}
      <Box align={align} direction="row" gap="xs" minWidth={0} shrink>
        {/* A flex column, never a block: a plain Text inside a block sits on
            the row's larger baseline strut and lands ~1.5px low, while a
            clamped one blockifies and centers — the facts rows read as
            uneven for exactly that. As a flex item every value keeps its own
            line box. Under `align="start"` the wrapper pads its first line
            onto the rail's centerline: (rung − line box) / 2. */}
        <Box
          minWidth={0}
          shrink
          direction="column"
          justify="center"
          paddingY={align === "start" ? (compact ? "xs" : "2xs") : undefined}
        >
          {children ?? (
            // A truncated value hands the rest over on hover; one that fits
            // never opens a bubble, so this stays off for the rows that wrap.
            <Text
              overflowTooltip
              size={resolvedValueSize}
              variant={hasValue ? undefined : "muted"}
              truncate={truncate}
            >
              {hasValue ? value : emptyText}
            </Text>
          )}
        </Box>
        {actions ? (
          <Box
            align="center"
            direction="row"
            gap="2xs"
            hoverReveal="row"
            shrink={false}
          >
            {actions}
          </Box>
        ) : null}
      </Box>
      {trailing ? (
        <Box marginLeft="auto" shrink={false}>
          {trailing}
        </Box>
      ) : null}
    </Box>
  );
}

function buildActions(
  value: string,
  label: string,
  link: DetailFieldRowProps["link"],
  copyable: boolean | undefined,
  onContact: DetailFieldRowProps["onContact"],
): React.ReactNode {
  if (link === "email") {
    return (
      <>
        <RowAction
          icon="mail"
          label="Send email"
          onClick={() => {
            onContact?.("email");
            openHref(`mailto:${value}`);
          }}
        />
        <RowAction
          icon="copy"
          label="Copy email"
          onClick={() => {
            onContact?.("copy");
            copyText(value, "Email");
          }}
        />
      </>
    );
  }
  if (link === "phone") {
    return (
      <>
        <RowAction
          icon="phone"
          label="Call"
          onClick={() => {
            onContact?.("call");
            openHref(`tel:${value}`);
          }}
        />
        <RowAction
          icon="copy"
          label="Copy phone"
          onClick={() => {
            onContact?.("copy");
            copyText(value, "Phone");
          }}
        />
      </>
    );
  }
  if (link === "url") {
    const href = urlHref(value);
    return (
      <>
        {href ? (
          <RowAction
            icon="external-link"
            label={`Open ${label.toLowerCase()}`}
            onClick={() => {
              onContact?.("open");
              openHref(href);
            }}
          />
        ) : null}
        <RowAction
          icon="copy"
          label={`Copy ${label.toLowerCase()}`}
          onClick={() => {
          onContact?.("copy");
          copyText(value, label);
        }}
        />
      </>
    );
  }
  if (copyable) {
    return (
      <RowAction
        icon="copy"
        label={`Copy ${label.toLowerCase()}`}
        onClick={() => {
          onContact?.("copy");
          copyText(value, label);
        }}
      />
    );
  }
  return null;
}
